<script>
/* eslint-env browser */
/* global FB */
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';

import ChannelApi from '../../../../../api/channels';
import { useBranding } from 'shared/composables/useBranding';
import NextButton from 'dashboard/components-next/button/Button.vue';

import { loadScript } from 'dashboard/helper/DOMHelpers';
import * as Sentry from '@sentry/vue';
// FORK_PATCH_BEGIN: channel-catalog
import { messengerOauthScopeString } from 'custom/dashboard/helper/channelCatalog';
// FORK_PATCH_END: channel-catalog
// FORK_PATCH_BEGIN: REQ-20260910-03 — batch add Messenger pages
import MessengerBatchAdd from 'custom/dashboard/extensions/messenger_batch_add/MessengerBatchAdd.vue';
// FORK_PATCH_END: REQ-20260910-03

export default {
  components: {
    LoadingState,
    NextButton,
    // FORK_PATCH_BEGIN: REQ-20260910-03
    MessengerBatchAdd,
    // FORK_PATCH_END: REQ-20260910-03
  },
  setup() {
    const { accountId } = useAccount();
    const { replaceInstallationName } = useBranding();
    return {
      accountId,
      replaceInstallationName,
    };
  },
  data() {
    return {
      hasError: false,
      user_access_token: '',
      pageList: [],
      emptyStateMessage: this.$t('INBOX_MGMT.DETAILS.LOADING_FB'),
      errorStateMessage: '',
      errorStateDescription: '',
      hasLoginStarted: false,
    };
  },

  computed: {
    showLoader() {
      return this.hasLoginStarted && !this.hasError && !this.user_access_token;
    },
  },

  mounted() {
    window.fbAsyncInit = this.runFBInit;
  },

  methods: {
    async startLogin() {
      this.hasLoginStarted = true;
      // FORK_PATCH: facebook-page-picker — clear prior empty list before re-auth
      this.hasError = false;
      this.user_access_token = '';
      this.pageList = [];
      try {
        await this.loadFBsdk();
        this.runFBInit();
        this.tryFBlogin();
      } catch (error) {
        if (error.name === 'ScriptLoaderError') {
          useAlert(this.$t('INBOX_MGMT.DETAILS.ERROR_FB_LOADING'));
        } else {
          Sentry.captureException(error);
          useAlert(this.$t('INBOX_MGMT.DETAILS.ERROR_FB_AUTH'));
        }
      }
    },

    runFBInit() {
      FB.init({
        appId: window.chatwootConfig.fbAppId,
        xfbml: true,
        version: window.chatwootConfig.fbApiVersion,
        status: true,
      });
      window.fbSDKLoaded = true;
      FB.AppEvents.logPageView();
    },

    async loadFBsdk() {
      return loadScript('https://connect.facebook.net/en_US/sdk.js', {
        id: 'facebook-jssdk',
      });
    },

    tryFBlogin() {
      FB.login(
        response => {
          this.hasError = false;
          if (response.status === 'connected') {
            this.fetchPages(response.authResponse.accessToken);
          } else if (response.status === 'not_authorized') {
            // eslint-disable-next-line no-console
            console.error('FACEBOOK AUTH ERROR', response);
            this.hasError = true;
            this.errorStateMessage = this.$t(
              'INBOX_MGMT.DETAILS.ERROR_FB_UNAUTHORIZED'
            );
            this.errorStateDescription = this.$t(
              'INBOX_MGMT.DETAILS.ERROR_FB_UNAUTHORIZED_HELP'
            );
          } else {
            // eslint-disable-next-line no-console
            console.error('FACEBOOK AUTH ERROR', response);
            this.hasError = true;
            this.errorStateMessage = this.$t(
              'INBOX_MGMT.DETAILS.ERROR_FB_AUTH'
            );
            this.errorStateDescription = '';
          }
        },
        {
          // FORK_PATCH_BEGIN: channel-catalog
          scope: messengerOauthScopeString(),
          // FORK_PATCH_END: channel-catalog
          // FORK_PATCH: facebook-page-picker — re-ask business asset grants
          auth_type: 'rerequest',
          return_scopes: true,
        }
      );
    },

    async fetchPages(_token) {
      try {
        const response = await ChannelApi.fetchFacebookPages(
          _token,
          this.accountId
        );
        const {
          data: { data },
        } = response;
        this.pageList = data.page_details || [];
        this.user_access_token = data.user_access_token;
      } catch (error) {
        // FORK_PATCH_BEGIN: facebook-page-picker
        this.hasError = true;
        this.errorStateMessage = this.$t('INBOX_MGMT.ADD.FB.NO_PAGES');
        this.errorStateDescription = this.$t('INBOX_MGMT.ADD.FB.NO_PAGES_HELP');
        // FORK_PATCH_END: facebook-page-picker
      }
    },
  },
};
</script>

<template>
  <div class="w-full h-full col-span-6 p-6 overflow-auto">
    <div
      v-if="!hasLoginStarted"
      class="flex flex-col items-center justify-center h-full text-center"
    >
      <a href="#" @click="startLogin()">
        <img
          class="w-auto h-10 rounded-md"
          src="~dashboard/assets/images/channels/facebook_login.png"
          alt="Facebook-logo"
        />
      </a>
      <p class="py-6">
        {{ replaceInstallationName($t('INBOX_MGMT.ADD.FB.HELP')) }}
      </p>
    </div>
    <div v-else>
      <div v-if="hasError" class="max-w-lg mx-auto text-center">
        <h5>{{ errorStateMessage }}</h5>
        <p
          v-if="errorStateDescription"
          v-dompurify-html="errorStateDescription"
        />
      </div>
      <LoadingState v-else-if="showLoader" :message="emptyStateMessage" />
      <!-- FORK_PATCH_BEGIN: facebook-page-picker -->
      <div
        v-else-if="!pageList.length"
        class="max-w-lg mx-auto text-center space-y-3"
      >
        <h5>{{ $t('INBOX_MGMT.ADD.FB.NO_PAGES') }}</h5>
        <p>{{ $t('INBOX_MGMT.ADD.FB.NO_PAGES_HELP') }}</p>
        <NextButton
          :label="$t('INBOX_MGMT.ADD.FB.RETRY_AUTH')"
          @click="startLogin"
        />
      </div>
      <!-- FORK_PATCH_END: facebook-page-picker -->
      <!-- FORK_PATCH_BEGIN: REQ-20260910-03 — batch add -->
      <MessengerBatchAdd
        v-else
        :pages="pageList"
        :user-access-token="user_access_token"
      />
      <!-- FORK_PATCH_END: REQ-20260910-03 -->
    </div>
  </div>
</template>
